import { api } from './api-client';
import { STORAGE } from './app-state';
import type { AppState, BirthDetailsInput } from '../types';

type SessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const SESSION_ID_PATTERN=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const validToken=(value:unknown):value is string=>typeof value==='string'&&value.length>=30&&value.length<=200;
export const MAX_READING_CREDENTIALS=50;

function credentialMap(storage:SessionStorage):Record<string,string>{
  try{
    const parsed=JSON.parse(storage.getItem(STORAGE.readingCredentials)??'{}') as unknown;
    if(!parsed||typeof parsed!=='object'||Array.isArray(parsed))return{};
    return Object.fromEntries(Object.entries(parsed).filter(([sessionId,token])=>SESSION_ID_PATTERN.test(sessionId)&&validToken(token))) as Record<string,string>;
  }catch{return{}}
}

export function rememberReadingCredential(sessionId:string,token:string,storage:SessionStorage=localStorage):void{
  if(!SESSION_ID_PATTERN.test(sessionId)||!validToken(token))return;
  const credentials=credentialMap(storage);delete credentials[sessionId];credentials[sessionId]=token;
  storage.setItem(STORAGE.readingCredentials,JSON.stringify(Object.fromEntries(Object.entries(credentials).slice(-MAX_READING_CREDENTIALS))));
}

export function readingCredentialFor(sessionId:string,storage:SessionStorage=localStorage):string|undefined{
  if(!SESSION_ID_PATTERN.test(sessionId))return undefined;
  const mapped=credentialMap(storage)[sessionId];if(mapped)return mapped;
  const legacySession=storage.getItem(STORAGE.session);const legacyToken=storage.getItem(STORAGE.resume);
  return legacySession===sessionId&&validToken(legacyToken)?legacyToken:undefined;
}

export function activeReadingCredential(storage:SessionStorage=localStorage):{sessionId?:string;token?:string}{
  const sessionId=storage.getItem(STORAGE.session)??undefined;
  if(!sessionId||!SESSION_ID_PATTERN.test(sessionId))return{};
  const token=readingCredentialFor(sessionId,storage);return token?{sessionId,token}:{sessionId};
}

export function persistSession(state: AppState,storage:SessionStorage=localStorage): void {
  if (state.resume_token) storage.setItem(STORAGE.resume, state.resume_token);
  if (state.reading_session_id) storage.setItem(STORAGE.session, state.reading_session_id);
  if(state.reading_session_id&&state.resume_token)rememberReadingCredential(state.reading_session_id,state.resume_token,storage);
}
export function clearSession(storage:SessionStorage=localStorage): void { storage.removeItem(STORAGE.resume); storage.removeItem(STORAGE.session); }
export type SaveProgressResponse = {
  saved: boolean;
  profile?: {
    first_name: string | null;
    gender_personalization?: AppState['gender_personalization'] | null;
  };
};
export async function saveProgress<T = SaveProgressResponse>(state: AppState, patch: Record<string, unknown>): Promise<T> {
  if (!state.resume_token) throw new Error('Your reading session could not be found.'); return api<T>('save-reading-progress', patch, state.resume_token);
}
type ProgressSaver=(state:AppState,patch:Record<string,unknown>)=>Promise<unknown>;
export async function persistBirthDetailsTransition(state:AppState,details:BirthDetailsInput,onSaved:(next:AppState)=>void,saver:ProgressSaver=saveProgress):Promise<AppState>{
  const next:AppState=details.birth_time_status==='unknown'
    ? {...state,...details,birth_hour:undefined,birth_minute:undefined,birth_meridiem:undefined,status:'details_completed'}
    : {...state,...details,status:'details_completed'};
  await saver(next,{...details,current_step:'focus',status:'details_completed'});
  onSaved(next);
  return next;
}
let debounceTimer: number | undefined; let pending: { state: AppState; patch: Record<string, unknown>; attempts: number } | undefined;
export function queueProgress(state: AppState, patch: Record<string, unknown>): void {
  if (!state.resume_token) return; pending={state,patch:{...(pending?.patch??{}),...patch},attempts:0};if(debounceTimer)clearTimeout(debounceTimer);debounceTimer=window.setTimeout(flush,500);
}
async function flush():Promise<void>{const job=pending;if(!job)return;pending=undefined;try{await saveProgress(job.state,job.patch)}catch{job.attempts++;if(job.attempts<=3){pending=job;debounceTimer=window.setTimeout(flush,1000*2**job.attempts)}}}
export function resumeTokenFromLocation(): string | undefined {
  const fragment = new URLSearchParams(location.hash.slice(1)); const token = fragment.get('resume') || fragment.get('token') || undefined;
  if (fragment.has('resume')) history.replaceState({}, '', `${location.pathname}${location.search}`); return token;
}
